/**
 * Analytics tracking utility for Nombox
 * Wraps Umami analytics with type-safe event tracking
 *
 * Features:
 * - Event batching to reduce network requests
 * - Retry logic with exponential backoff
 * - Persistent queue in localStorage for crash recovery
 * - Preserves original timestamps and event data
 *
 * Note: All requests go through /api/analytics proxy (no CORS issues!)
 */

import { IS_PRODUCTION } from "@/lib/env";
import {
  clearAnalyticsQueue,
  loadAnalyticsQueue,
  type QueuedAnalyticsEvent,
  saveAnalyticsQueue,
} from "@/lib/storage";

// Extend Window interface to include Umami
declare global {
  interface Window {
    umami?: {
      track: (eventName: string, eventData?: Record<string, any>) => void;
    };
  }
}

/**
 * Configuration for analytics batching
 */
const BATCH_CONFIG = {
  BATCH_SIZE: 10, // Max events per batch
  BATCH_INTERVAL: 60 * 1000, // Process queue every min
  MAX_RETRIES: 3, // Max retry attempts per event
  RETRY_BACKOFF: 1000, // Base backoff time in ms (exponential)
  MAX_QUEUE_SIZE: 100, // Max events to store (prevent memory issues)
} as const;

/**
 * Analytics Queue Manager
 * Handles batching, retry logic, and persistence
 */
class AnalyticsQueueManager {
  private queue: QueuedAnalyticsEvent[] = [];
  private processing = false;
  private intervalId: NodeJS.Timeout | null = null;
  private isInitialized = false;

  constructor() {
    if (typeof window !== "undefined") {
      this.initialize();
    }
  }

  /**
   * Initialize the queue manager
   * - Load persisted queue from localStorage
   * - Start batch processing interval
   * - Setup page visibility and beforeunload handlers
   */
  private initialize() {
    if (this.isInitialized) return;

    // Load persisted queue
    this.queue = loadAnalyticsQueue();
    if (this.queue.length > 0) {
      console.log(`📊 Loaded ${this.queue.length} queued analytics events`);
    }

    // Start processing interval
    this.startProcessing();

    // Process queue when page becomes visible
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        this.processQueue();
      }
    });

    // Flush queue before page unload
    window.addEventListener("beforeunload", () => {
      this.flush();
    });

    this.isInitialized = true;
  }

  /**
   * Add event to queue
   */
  enqueue(eventName: string, eventData?: Record<string, any>) {
    // Prevent queue from growing too large
    if (this.queue.length >= BATCH_CONFIG.MAX_QUEUE_SIZE) {
      console.warn("⚠️ Analytics queue full, dropping oldest events");
      this.queue = this.queue.slice(-BATCH_CONFIG.MAX_QUEUE_SIZE / 2);
    }

    const event: QueuedAnalyticsEvent = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      eventName,
      eventData,
      timestamp: Date.now(),
      retryCount: 0,
    };

    this.queue.push(event);
    this.persistQueue();

    if (!IS_PRODUCTION) {
      console.log("📊 Analytics Event Queued:", eventName, eventData);
    }
  }

  /**
   * Start batch processing interval
   */
  private startProcessing() {
    if (this.intervalId) return;

    this.intervalId = setInterval(() => {
      this.processQueue();
    }, BATCH_CONFIG.BATCH_INTERVAL);
  }

  /**
   * Stop batch processing interval
   */
  private stopProcessing() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Process queued events in batches
   */
  private async processQueue() {
    if (this.processing || this.queue.length === 0) return;

    if (!navigator.onLine) {
      if (!IS_PRODUCTION) {
        console.warn("⚠️ Offline, keeping events in queue");
      }
      return;
    }

    if (!window.umami) {
      if (!IS_PRODUCTION) {
        console.warn("⚠️ Umami not loaded yet, keeping events in queue");
      }
      return;
    }

    this.processing = true;

    try {
      // Get events ready to be sent (not recently failed)
      const now = Date.now();
      const readyEvents = this.queue.filter((event) => {
        if (!event.lastAttempt) return true;

        // Exponential backoff: wait longer after each retry
        const backoffTime = BATCH_CONFIG.RETRY_BACKOFF * Math.pow(2, event.retryCount);
        return now - event.lastAttempt >= backoffTime;
      });

      if (readyEvents.length === 0) {
        this.processing = false;
        return;
      }

      // Process in batches
      const batch = readyEvents.slice(0, BATCH_CONFIG.BATCH_SIZE);
      const results = await this.sendBatch(batch);

      // Remove successful events, update failed ones
      this.queue = this.queue.filter((event) => {
        const result = results.find((r) => r.id === event.id);
        if (!result) return true; // Keep events not in this batch

        if (result.success) {
          return false; // Remove successful events
        } else {
          // Update retry count and last attempt
          event.retryCount++;
          event.lastAttempt = Date.now();

          // Remove if exceeded max retries
          if (event.retryCount >= BATCH_CONFIG.MAX_RETRIES) {
            console.error("❌ Analytics event failed after max retries:", event.eventName);
            return false;
          }

          return true; // Keep for retry
        }
      });

      this.persistQueue();

      if (!IS_PRODUCTION) {
        const successful = results.filter((r) => r.success).length;
        console.log(`📊 Batch processed: ${successful}/${batch.length} successful`);
      }
    } catch (error) {
      console.error("❌ Error processing analytics queue:", error);
    } finally {
      this.processing = false;
    }
  }

  /**
   * Send a batch of events to Umami
   */
  private async sendBatch(
    batch: QueuedAnalyticsEvent[]
  ): Promise<Array<{ id: string; success: boolean }>> {
    const results = await Promise.allSettled(
      batch.map(async (event) => {
        try {
          // Send event with original timestamp preserved in event data
          window.umami!.track(event.eventName, {
            ...event.eventData,
            _timestamp: event.timestamp, // Preserve original timestamp
            _retryCount: event.retryCount,
          });

          return { id: event.id, success: true };
        } catch (error) {
          console.error("❌ Failed to send analytics event:", event.eventName, error);
          return { id: event.id, success: false };
        }
      })
    );

    return results.map((result, index) => {
      if (result.status === "fulfilled") {
        return result.value;
      } else {
        return { id: batch[index].id, success: false };
      }
    });
  }

  /**
   * Flush all queued events immediately (best effort)
   * Used before page unload
   */
  private flush() {
    if (this.queue.length === 0 || !window.umami) return;

    // Send all queued events synchronously (best effort)
    this.queue.forEach((event) => {
      try {
        window.umami!.track(event.eventName, {
          ...event.eventData,
          _timestamp: event.timestamp,
          _flushed: true,
        });
      } catch {
        // Silent fail during unload
      }
    });

    // Clear queue
    this.queue = [];
    clearAnalyticsQueue();
  }

  /**
   * Persist queue to localStorage
   */
  private persistQueue() {
    saveAnalyticsQueue(this.queue);
  }

  /**
   * Get current queue size (for debugging)
   */
  getQueueSize(): number {
    return this.queue.length;
  }

  /**
   * Manually trigger queue processing (for testing)
   */
  async processNow() {
    await this.processQueue();
  }
}

// Singleton instance
let queueManager: AnalyticsQueueManager | null = null;

/**
 * Get or create the queue manager instance
 */
function getQueueManager(): AnalyticsQueueManager {
  if (!queueManager) {
    queueManager = new AnalyticsQueueManager();
  }
  return queueManager;
}

/**
 * Track custom events in Umami (with batching)
 * @param eventName - Name of the event (e.g., 'subscription_completed')
 * @param eventData - Additional data to track with the event
 */
export function trackEvent(eventName: string, eventData?: Record<string, any>) {
  if (typeof window === "undefined") return;

  try {
    const manager = getQueueManager();
    manager.enqueue(eventName, eventData);
  } catch (error) {
    console.error("❌ Failed to queue event:", eventName, error);
  }
}

/**
 * Get current analytics queue size (for debugging)
 */
export function getAnalyticsQueueSize(): number {
  if (typeof window === "undefined") return 0;
  return getQueueManager().getQueueSize();
}

/**
 * Manually process analytics queue (for testing)
 */
export async function processAnalyticsQueue() {
  if (typeof window === "undefined") return;
  await getQueueManager().processNow();
}

/**
 * Analytics Events
 * Centralized event definitions for type safety and documentation
 */
export const AnalyticsEvents = {
  // Subscription Events (Premium Conversions)
  SUBSCRIPTION_MODAL_OPENED: "subscription_modal_opened",
  SUBSCRIPTION_CHECKOUT_STARTED: "subscription_checkout_started",
  SUBSCRIPTION_COMPLETED: "subscription_completed",
  SUBSCRIPTION_CANCELLED: "subscription_cancelled",
  SUBSCRIPTION_CHANGED: "subscription_changed",

  // Ingredient Events
  INGREDIENT_ADDED: "ingredient_added",
  INGREDIENT_ARCHIVED: "ingredient_archived",
  INGREDIENT_RESTORED: "ingredient_restored",
  INGREDIENT_DELETED: "ingredient_deleted",
  INGREDIENT_EDITED: "ingredient_edited",

  // App Engagement Events
  ARCHIVE_OPENED: "archive_opened",
  SYNC_MANUAL: "sync_manual",
  THEME_CHANGED: "theme_changed",
  SETTINGS_OPENED: "settings_opened",
  ARCHIVE_ALL: "archive_all",
  DELETE_ALL: "delete_all",

  // Limit Events (Conversion Indicators)
  // Voice / Microphone Events
  VOICE_INPUT_TAPPED: "voice_input_tapped",

  // Account Events
  ACCOUNT_DELETED: "account_deleted",

  // Recipe Events
  RECIPE_CREATED: "recipe_created",
  RECIPE_EDITED: "recipe_edited",
  RECIPE_DELETED: "recipe_deleted",
  RECIPE_FAVORITED: "recipe_favorited",
  RECIPE_UNFAVORITED: "recipe_unfavorited",
  RECIPE_URL_IMPORTED: "recipe_url_imported",
  RECIPE_PUBLISHED: "recipe_published",
  RECIPE_UNPUBLISHED: "recipe_unpublished",

  // Calendar Events
  MEAL_PLANNED: "meal_planned",
  MEAL_REMOVED: "meal_removed",

  // Search Events
  RECIPE_SEARCHED: "recipe_searched",

  // Sharing Events
  INVITATION_SENT: "invitation_sent",
  INVITATION_ACCEPTED: "invitation_accepted",
  CALENDAR_INVITATION_SENT: "calendar_invitation_sent",
  CALENDAR_INVITATION_ACCEPTED: "calendar_invitation_accepted",
  REFERRAL_SENT: "referral_sent",
  REFERRAL_ACCEPTED: "referral_accepted",

  // List Events
  LIST_CREATED: "list_created",
  LIST_DELETED: "list_deleted",

  // Auth Events
  SIGNIN_COMPLETED: "signin_completed",

  // Prep Ahead Events
  PREP_AHEAD_GENERATED: "prep_ahead_generated",

  // Meal Suggestion Events
  MEAL_SUGGEST_GENERATED: "meal_suggest_generated",
  MEAL_SUGGEST_DAY_REGENERATED: "meal_suggest_day_regenerated",
  MEAL_SUGGEST_CONFIRMED: "meal_suggest_confirmed",

  // Recipe AI Chat Events
  RECIPE_CHAT_OPENED: "recipe_chat_opened",
  RECIPE_CHAT_MESSAGE_SENT: "recipe_chat_message_sent",
  RECIPE_CHAT_CHANGES_APPLIED: "recipe_chat_changes_applied",
  RECIPE_CHAT_CHANGES_DISMISSED: "recipe_chat_changes_dismissed",
  AI_RECIPE_EDIT_APPLIED: "ai_recipe_edit_applied",
} as const;

/**
 * Track subscription events (key conversion metric)
 */
export function trackSubscription(
  action: "modal_opened" | "checkout_started" | "completed" | "cancelled" | "changed",
  data?: {
    interval?: "monthly" | "yearly";
    tier?: string;
    limitType?: "ai" | null;
    previousInterval?: "monthly" | "yearly";
  }
) {
  const eventMap = {
    modal_opened: AnalyticsEvents.SUBSCRIPTION_MODAL_OPENED,
    checkout_started: AnalyticsEvents.SUBSCRIPTION_CHECKOUT_STARTED,
    completed: AnalyticsEvents.SUBSCRIPTION_COMPLETED,
    cancelled: AnalyticsEvents.SUBSCRIPTION_CANCELLED,
    changed: AnalyticsEvents.SUBSCRIPTION_CHANGED,
  };

  trackEvent(eventMap[action], data);
}

/**
 * Track ingredient actions
 * @param action - Type of ingredient action
 * @param data - Event data
 * @param data.category - Ingredient category
 * @param data.hasQuantity - Whether ingredient has quantity
 * @param data.count - Number of ingredients affected (for bulk operations)
 */
export function trackIngredient(
  action: "added" | "archived" | "restored" | "deleted" | "edited",
  data?: {
    category?: string;
    hasQuantity?: boolean;
    count?: number; // NEW: For bulk operations
  }
) {
  const eventMap = {
    added: AnalyticsEvents.INGREDIENT_ADDED,
    archived: AnalyticsEvents.INGREDIENT_ARCHIVED,
    restored: AnalyticsEvents.INGREDIENT_RESTORED,
    deleted: AnalyticsEvents.INGREDIENT_DELETED,
    edited: AnalyticsEvents.INGREDIENT_EDITED,
  };

  trackEvent(eventMap[action], data);
}

/**
 * Track app engagement
 */
export function trackEngagement(
  action:
    | "archive_opened"
    | "sync_manual"
    | "theme_changed"
    | "settings_opened"
    | "archive_all"
    | "delete_all",
  data?: {
    theme?: "light" | "dark" | "system";
  }
) {
  const eventMap = {
    archive_opened: AnalyticsEvents.ARCHIVE_OPENED,
    sync_manual: AnalyticsEvents.SYNC_MANUAL,
    theme_changed: AnalyticsEvents.THEME_CHANGED,
    settings_opened: AnalyticsEvents.SETTINGS_OPENED,
    archive_all: AnalyticsEvents.ARCHIVE_ALL,
    delete_all: AnalyticsEvents.DELETE_ALL,
  };

  trackEvent(eventMap[action], data);
}

/**
 * Identify user for analytics (call after authentication)
 * This allows Umami to track user-level metrics
 */
export function identifyUser(userId: string, traits?: Record<string, any>) {
  // Umami automatically tracks users, but we can add custom properties
  trackEvent("user_identified", {
    userId,
    ...traits,
  });
}

/**
 * Track that the user tapped the voice input / microphone button.
 * This records only the occurrence (no extra payload) and is used
 * in multiple UI locations.
 */
export function trackVoiceInputTapped() {
  trackEvent(AnalyticsEvents.VOICE_INPUT_TAPPED);
}

/**
 * Track account deletion
 * Records user ID and email before account is deleted
 */
export function trackAccountDeleted(data: { userId: string; email: string | null | undefined }) {
  trackEvent(AnalyticsEvents.ACCOUNT_DELETED, {
    userId: data.userId,
    email: data.email || "unknown",
  });
}

export function trackRecipe(
  action:
    | "created"
    | "edited"
    | "deleted"
    | "favorited"
    | "unfavorited"
    | "url_imported"
    | "published"
    | "unpublished",
  data?: {
    recipeId?: string;
    hasImage?: boolean;
    tagCount?: number;
    ingredientCount?: number;
    directionCount?: number;
    source?: string;
  }
) {
  const eventMap = {
    created: AnalyticsEvents.RECIPE_CREATED,
    edited: AnalyticsEvents.RECIPE_EDITED,
    deleted: AnalyticsEvents.RECIPE_DELETED,
    favorited: AnalyticsEvents.RECIPE_FAVORITED,
    unfavorited: AnalyticsEvents.RECIPE_UNFAVORITED,
    url_imported: AnalyticsEvents.RECIPE_URL_IMPORTED,
    published: AnalyticsEvents.RECIPE_PUBLISHED,
    unpublished: AnalyticsEvents.RECIPE_UNPUBLISHED,
  };

  trackEvent(eventMap[action], data);
}

export function trackCalendar(action: "meal_planned" | "meal_removed", data?: { cookOn?: string }) {
  const eventMap = {
    meal_planned: AnalyticsEvents.MEAL_PLANNED,
    meal_removed: AnalyticsEvents.MEAL_REMOVED,
  };

  trackEvent(eventMap[action], data);
}

export function trackSearch(data: { query: string; source: "recipes" | "discover" }) {
  trackEvent(AnalyticsEvents.RECIPE_SEARCHED, data);
}

export function trackSharing(
  action: "invitation_sent" | "invitation_accepted" | "referral_sent" | "referral_accepted",
  data?: { listId?: string }
) {
  const eventMap = {
    invitation_sent: AnalyticsEvents.INVITATION_SENT,
    invitation_accepted: AnalyticsEvents.INVITATION_ACCEPTED,
    referral_sent: AnalyticsEvents.REFERRAL_SENT,
    referral_accepted: AnalyticsEvents.REFERRAL_ACCEPTED,
  };

  trackEvent(eventMap[action], data);
}

export function trackCalendarSharing(action: "invitation_sent" | "invitation_accepted") {
  const eventMap = {
    invitation_sent: AnalyticsEvents.CALENDAR_INVITATION_SENT,
    invitation_accepted: AnalyticsEvents.CALENDAR_INVITATION_ACCEPTED,
  };

  trackEvent(eventMap[action]);
}

export function trackList(action: "created" | "deleted") {
  const eventMap = {
    created: AnalyticsEvents.LIST_CREATED,
    deleted: AnalyticsEvents.LIST_DELETED,
  };

  trackEvent(eventMap[action]);
}

export function trackSigninCompleted(data: { provider: string }) {
  trackEvent(AnalyticsEvents.SIGNIN_COMPLETED, data);
}

export function trackPrepAheadGenerated() {
  trackEvent(AnalyticsEvents.PREP_AHEAD_GENERATED);
}

export function trackMealSuggest(
  action: "generated" | "day_regenerated" | "confirmed",
  data?: { count?: number }
) {
  const eventMap = {
    generated: AnalyticsEvents.MEAL_SUGGEST_GENERATED,
    day_regenerated: AnalyticsEvents.MEAL_SUGGEST_DAY_REGENERATED,
    confirmed: AnalyticsEvents.MEAL_SUGGEST_CONFIRMED,
  };
  trackEvent(eventMap[action], data);
}

export function trackRecipeChat(
  action: "opened" | "message_sent" | "changes_applied" | "changes_dismissed",
  data?: { mode?: "create" | "edit" | "mobile" | "desktop"; messageCount?: number }
) {
  const eventMap = {
    opened: AnalyticsEvents.RECIPE_CHAT_OPENED,
    message_sent: AnalyticsEvents.RECIPE_CHAT_MESSAGE_SENT,
    changes_applied: AnalyticsEvents.RECIPE_CHAT_CHANGES_APPLIED,
    changes_dismissed: AnalyticsEvents.RECIPE_CHAT_CHANGES_DISMISSED,
  };

  trackEvent(eventMap[action], data);
}

export function trackAiRecipeEditApplied(data: { mode: "create" | "edit" }) {
  trackEvent(AnalyticsEvents.AI_RECIPE_EDIT_APPLIED, data);
}
